import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';

import Panel from '../ui/Panel';
import JiraConfigStatus from './JiraConfigStatus';

const ENV_VARS = [
  ['JIRA_BASE_URL', 'Your Jira instance URL (e.g., https://your-domain.atlassian.net)'],
  ['JIRA_API_EMAIL', 'Email address associated with your Jira account'],
  ['JIRA_API_TOKEN', 'API token created in your Atlassian account settings'],
  [
    'JIRA_JQL_QUERY (optional)',
    'JQL query to filter issues (default: project = "YOUR_PROJECT_KEY" ORDER BY updated DESC)',
  ],
];

/**
 * Jira connection: credential test, config inspection and manual sync.
 * All handlers and loading flags are owned by the Admin page.
 */
const JiraSection = ({
  testLoading,
  syncLoading,
  onTestConnection,
  onSync,
  databaseAvailable,
  jiraConfigStatus,
  jiraConfigLoading,
  jiraConfigExpanded,
  setJiraConfigExpanded,
  fetchJiraConfigStatus,
}) => (
  <Panel
    title="Jira Configuration"
    subtitle="Test the connection to your Jira instance using the credentials configured on the server."
  >
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
      <Button
        variant="outlined"
        onClick={onTestConnection}
        disabled={testLoading || syncLoading}
        startIcon={testLoading ? <CircularProgress size={18} color="inherit" /> : <VpnKeyIcon />}
      >
        {testLoading ? 'Testing…' : 'Test Jira Connection'}
      </Button>
      <Button
        variant="outlined"
        onClick={() => {
          setJiraConfigExpanded(!jiraConfigExpanded);
          if (!jiraConfigExpanded && !jiraConfigStatus) fetchJiraConfigStatus();
        }}
        disabled={jiraConfigLoading}
        startIcon={jiraConfigLoading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
      >
        {jiraConfigLoading ? 'Loading…' : 'View Configuration Status'}
      </Button>
    </Stack>

    {jiraConfigStatus && (
      <Accordion
        expanded={jiraConfigExpanded}
        onChange={(e, expanded) => setJiraConfigExpanded(expanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleOutlineIcon
              fontSize="small"
              sx={{
                color:
                  jiraConfigStatus.baseUrlSet &&
                  jiraConfigStatus.apiEmailSet &&
                  jiraConfigStatus.apiTokenSet
                    ? 'success.main'
                    : 'warning.main',
              }}
            />
            <Typography variant="subtitle2">Configuration Status</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <JiraConfigStatus status={jiraConfigStatus} />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={fetchJiraConfigStatus}
              disabled={jiraConfigLoading}
              startIcon={
                jiraConfigLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />
              }
            >
              Refresh
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
    )}

    <Accordion sx={{ mt: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HelpOutlineIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" color="primary">
            How to configure the Jira connection
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary">
          Set the following environment variables on your server:
        </Typography>
        <List dense>
          {ENV_VARS.map(([name, description]) => (
            <ListItem key={name} disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleOutlineIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={name} secondary={description} />
            </ListItem>
          ))}
        </List>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          For local development add these to a <code>.env</code> file in the server directory. For
          production, set them in your deployment environment.
        </Typography>
        <Link
          href="https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/"
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
        >
          Learn how to create a Jira API token
        </Link>
      </AccordionDetails>
    </Accordion>

    <Divider sx={{ my: 3 }} />

    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
      Jira Synchronization
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
      Manually trigger a synchronization with Jira to fetch the latest issue data.
      {!databaseAvailable && (
        <Box component="span" sx={{ color: 'warning.main', fontWeight: 600, ml: 0.5 }}>
          (Requires database connection)
        </Box>
      )}
    </Typography>
    <Button
      variant="contained"
      onClick={onSync}
      disabled={syncLoading || testLoading || !databaseAvailable}
      startIcon={syncLoading ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
    >
      {syncLoading ? 'Syncing…' : 'Sync Jira Issues'}
    </Button>
  </Panel>
);

export default JiraSection;
